import { isInIframe } from 'js/lib/partitionCookieDetect';
import { isAuthenticatedUser } from 'js/lib/user';

const STORAGE_KEY = 'embeddedExperience.launchContext';
const VIDEO_PATH = /^\/videos\//;
// Module-home launch, e.g. /learn/<slug>/home/module/4 (legacy /home/week/4); captures the 1-based index.
const MODULE_HOME_PATH = /\/home\/(?:module|week)\/(\d+)/;
// Single-item launches (labs, readings, quizzes, LTI, …) land on the generic item deep-link route.
const ITEM_LAUNCH_PATH = /\/learn\/[^/]+\/item\/([^/?]+)/;

export type EmbedScope = 'course' | 'module' | 'item';

export type EmbeddedLaunchContext = {
  /** Launch scope; undefined when not launched as an embed. course = full outline, module = single
   * module, item = single item / assignment / video. */
  embedScope?: EmbedScope;
  moduleId?: string;
  /** 1-based module index from the module-home launch URL (when known). */
  moduleNumber?: number;
  itemId?: string;
  /** Org product-feature gate, resolved at LTI login via `persistEmbedAllowedForOrg`. */
  isEmbedAllowedForOrg?: boolean;
  /** Org product-feature gate for hiding the Help button, resolved at LTI login via
   * `persistHelpButtonDisabledForOrg`. Read downstream by any embedded surface (course items,
   * Clips) without those surfaces needing to know the org id themselves. */
  isHelpButtonDisabledForOrg?: boolean;
  /** The specialization the embed launched into, captured at the s12n landing-page redirect via
   * `persistEmbeddedS12nId`. A course can belong to more than one s12n, so only the launch-time
   * id is unambiguous. */
  s12nId?: string;
  /** Third-party org slug, captured at LTI login via `persistEmbeddedOrgSlug`. */
  organizationSlug?: string;
};

const NON_EMBED_VIEW: EmbeddedLaunchContext = {};

let contextCache: EmbeddedLaunchContext | undefined;

const readFromUrl = (): EmbeddedLaunchContext | undefined => {
  const params = new URLSearchParams(window.location.search);
  const isExternal = params.get('isExternal') === 'true';
  const deepLinkItemId = params.get('deepLinkItemId') ?? undefined;
  const deepLinkModuleId = params.get('deepLinkModuleId') ?? undefined;
  // Set by UnifiedSSOActionContainer's post-login redirect, so org context survives that hop
  // even when sessionStorage (the primary carrier) isn't available.
  const organizationSlug = params.get('orgSlug') ?? undefined;
  const isVideoRoute = VIDEO_PATH.test(window.location.pathname);
  const moduleHomeMatch = MODULE_HOME_PATH.exec(window.location.pathname);
  const itemLaunchMatch = ITEM_LAUNCH_PATH.exec(window.location.pathname);

  if (!isExternal && !isVideoRoute) {
    return undefined;
  }

  // A single item / assignment (/learn/<slug>/item/<id> or ?deepLinkItemId=) and a
  // standalone video are all "single item" embeds — no surrounding course outline.
  if (deepLinkItemId || isVideoRoute || itemLaunchMatch) {
    const itemId = deepLinkItemId ?? itemLaunchMatch?.[1] ?? window.location.pathname.split('/').filter(Boolean).pop();
    return { embedScope: 'item', itemId, organizationSlug };
  }
  if (deepLinkModuleId) {
    return { embedScope: 'module', moduleId: deepLinkModuleId, organizationSlug };
  }
  if (moduleHomeMatch) {
    return { embedScope: 'module', moduleNumber: Number(moduleHomeMatch[1]), organizationSlug };
  }
  return { embedScope: 'course', organizationSlug };
};

const persist = (context: EmbeddedLaunchContext) => {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(context));
  } catch {
    // sessionStorage may be unavailable (private mode / blocked storage) — ignore.
  }
};

const readFromSession = (): EmbeddedLaunchContext | undefined => {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as EmbeddedLaunchContext) : undefined;
  } catch {
    return undefined;
  }
};

/**
 * Memoized for the page load. Must be called before the item redirector rewrites the URL — the
 * launch params are only present on that first hit.
 */
export const resolveEmbeddedLaunchContext = (): EmbeddedLaunchContext => {
  if (typeof window === 'undefined') {
    return NON_EMBED_VIEW;
  }
  if (contextCache) {
    return contextCache;
  }
  const fromUrl = readFromUrl();
  if (fromUrl) {
    // Preserve fields resolved before the launch redirect (org gate from SSOReturnPage, s12n id
    // from the landing page) across the URL re-parse.
    const session = readFromSession();
    const context = {
      ...fromUrl,
      // A signed-in page only renders in the LMS frame if edge allowed this org to be framed, so a
      // relaunch that skipped sign-in (and so SSOReturnPage) can still mark the org gate as on.
      isEmbedAllowedForOrg:
        session?.isEmbedAllowedForOrg ??
        (new URLSearchParams(window.location.search).get('isExternal') === 'true' &&
        isInIframe() &&
        isAuthenticatedUser()
          ? true
          : undefined),
      isHelpButtonDisabledForOrg: session?.isHelpButtonDisabledForOrg,
      s12nId: session?.s12nId,
      organizationSlug: fromUrl.organizationSlug ?? session?.organizationSlug,
    };
    persist(context);
    contextCache = context;
  } else {
    contextCache = readFromSession() ?? NON_EMBED_VIEW;
  }
  return contextCache;
};

export const persistEmbedAllowedForOrg = (isEmbedAllowedForOrg: boolean): void => {
  if (!isInIframe()) {
    return;
  }
  const context = { ...resolveEmbeddedLaunchContext(), isEmbedAllowedForOrg };
  contextCache = context;
  persist(context);
};

export const persistHelpButtonDisabledForOrg = (isHelpButtonDisabledForOrg: boolean): void => {
  if (!isInIframe()) {
    return;
  }
  const context = { ...resolveEmbeddedLaunchContext(), isHelpButtonDisabledForOrg };
  contextCache = context;
  persist(context);
};

export const persistEmbeddedS12nId = (s12nId: string): void => {
  if (!isInIframe()) {
    return;
  }
  const context = { ...resolveEmbeddedLaunchContext(), s12nId };
  contextCache = context;
  persist(context);
};

export const readEmbeddedS12nId = (): string | undefined => resolveEmbeddedLaunchContext().s12nId;

export const persistEmbeddedOrgSlug = (organizationSlug: string): void => {
  if (!isInIframe()) {
    return;
  }
  const context = { ...resolveEmbeddedLaunchContext(), organizationSlug };
  contextCache = context;
  persist(context);
};

export const readEmbeddedOrgSlug = (): string | undefined => resolveEmbeddedLaunchContext().organizationSlug;

/** Test-only: clear the page-load memoization. */
export const _resetEmbeddedLaunchContextForTests = (): void => {
  contextCache = undefined;
};

// Capture at module init, while the URL is still the launch URL. The enroll/redirect flow and the
// item redirector strip ?isExternal before Course Frame mounts, so reading the launch params after
// any component has mounted would run too late.
if (typeof window !== 'undefined') {
  try {
    resolveEmbeddedLaunchContext();
  } catch {
    // best-effort; never block module init
  }
}
